import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import { isArray, pick } from 'lodash';
import { MouseEvent, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import {
  AdvisorDetails,
  AssociationEntityDetails,
  CompanyEntityDetails,
  EntityWithDocumentFullResponse,
  IndividualEntityDetails,
  PartnershipEntityDetails,
  TaxDetails,
  TrustEntityDetails,
  WholesaleDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import { InvestorEditsExtendFull } from '@/modules/entity/types/InvestorEditByIdResponse.ts';
import { addEnumToValue } from '@/modules/entity/utils/entity-info/entity-info.tsx';
import { HolderRejectEntityModal } from '@/modules/issuer-dashboard/components/holder-create/RejectEntityModal.tsx';
import { useInvestorEditUpdateStatusMutation } from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  Alert,
  Button,
  DisclosureListItem,
  ModalProviderContext,
  Select,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { AlertBlueIcon, AlertWhiteIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import { EntityType, InvestorEditsStatus } from 'api';

enum TabHolderInfo {
  ContactDetails = 'Contact Details',
  BankDetails = 'Bank Details',
  IndividualDetails = 'Individual Details',
  CompanyDetails = 'Company Details',
  TrustDetails = 'Trust Details',
  SmsfDetails = 'SMSF Details',
  PartnershipDetails = 'Partnership Details',
  AssociationDetails = 'Association Details',
  TaxInformation = 'Tax information',
  AdvisorDetails = 'Advisor Details',
  WholesaleDetails = 'Wholesale Details',
}

export interface HolderEditItemProps {
  item: InvestorEditsExtendFull;
}

export const HolderEditItem = ({ item }: HolderEditItemProps) => {
  const [currentInfoTab, setCurrentInfoTab] = useState<TabHolderInfo>(TabHolderInfo.ContactDetails);
  const userRole = rolesManipulator.current_user_role;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const status = item?.status;
  const entity_application_rejection_reason =
    item?.new_data?.entity_data?.entity_application_rejection_reason;
  const { openModal } = useContext(ModalProviderContext);

  const { mutateAsync: mutateAsyncChangeStatus, isLoading: isLoadingChangeStatus } =
    useInvestorEditUpdateStatusMutation();

  const handleOpenRejectEntityModal = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      openModal(HolderRejectEntityModal, { id: item.id });
    },
    [item.id, openModal],
  );

  const handleContinueEdit = useCallback(() => {
    params.set('step', '1');
    navigate(
      {
        pathname: issuerDashboardPaths.dynamic.updateHolder(String(item.id)),
        search: params.toString(),
      },
      { replace: false, state: { from: pathname + '?' + params.toString(), action: 'update' } },
    );
  }, [item.id, navigate, params, pathname]);

  const handleChangeStatus = useCallback(
    (status: InvestorEditsStatus, description: string | undefined = undefined) =>
      () => {
        mutateAsyncChangeStatus({
          id: item.id,
          status,
          description,
        });
      },
    [item.id, mutateAsyncChangeStatus],
  );

  const bottomCustomContent = useMemo<JSX.Element>(() => {
    const oldEntity = (item?.old_data?.entity_data || {}) as EntityWithDocumentFullResponse;
    const newEntity = (item?.new_data?.entity_data || {}) as EntityWithDocumentFullResponse;

    const contactsOld = pick<EntityWithDocumentFullResponse, keyof EntityWithDocumentFullResponse>(
      oldEntity,
      [
        'notifications_enabled',
        'email',
        'phone_number',
        'address',
        'additional_address',
        'suburb',
        'state',
        'postcode',
        'country',
        'additional_emails',
      ],
    );
    const contactsNew = pick<EntityWithDocumentFullResponse, keyof EntityWithDocumentFullResponse>(
      newEntity,
      [
        'notifications_enabled',
        'email',
        'phone_number',
        'address',
        'additional_address',
        'suburb',
        'state',
        'postcode',
        'country',
        'additional_emails',
      ],
    );

    const contactsDetails = (
      <>
        {Object.entries(contactsNew).length > 0 ? (
          Object.entries(contactsNew).map(([key, newValue], i) => {
            const oldValue = (contactsOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Contacts details</div>
        )}
      </>
    );

    const bankNew = pick<EntityWithDocumentFullResponse, keyof EntityWithDocumentFullResponse>(
      newEntity,
      ['bank_account_bsb', 'bank_name', 'bank_account_number', 'bank_account_name'],
    );
    const bankOld = pick<EntityWithDocumentFullResponse, keyof EntityWithDocumentFullResponse>(
      oldEntity,
      ['bank_account_bsb', 'bank_name', 'bank_account_number', 'bank_account_name'],
    );

    const bankDetails = (
      <>
        {Object.entries(bankNew).length > 0 ? (
          Object.entries(bankNew).map(([key, newValue], i) => {
            const oldValue = (bankOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Bank details</div>
        )}
      </>
    );

    const individualNew = pick<IndividualEntityDetails, keyof IndividualEntityDetails>(
      newEntity?.individual_entity_details || {},
      [
        'gender',
        'title',
        'first_name',
        'last_name',
        'middle_name',
        'date_of_birth',
        'country',
        'state',
        'suburb',
        'locality',
        'postcode',
      ],
    );
    const individualOld = pick<IndividualEntityDetails, keyof IndividualEntityDetails>(
      oldEntity?.individual_entity_details || {},
      [
        'gender',
        'title',
        'first_name',
        'last_name',
        'middle_name',
        'date_of_birth',
        'country',
        'state',
        'suburb',
        'locality',
        'postcode',
      ],
    );

    const individualDetails = (
      <>
        {Object.entries(individualNew).length > 0 ? (
          Object.entries(individualNew).map(([key, newValue], i) => {
            const oldValue = (individualOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Individual details</div>
        )}
      </>
    );

    const companyNew = pick<CompanyEntityDetails, keyof CompanyEntityDetails>(
      newEntity?.company_entity_details || {},
      [
        'company_name',
        'entity_number',
        'registered_office',
        'principal_place_of_business',
        'country',
        'abn',
        'acn',
        'nzbn',
      ],
    );
    const companyOld = pick<CompanyEntityDetails, keyof CompanyEntityDetails>(
      oldEntity?.company_entity_details || {},
      [
        'company_name',
        'entity_number',
        'registered_office',
        'principal_place_of_business',
        'country',
        'abn',
        'acn',
        'nzbn',
      ],
    );

    const companyDetails = (
      <>
        {Object.entries(companyNew).length > 0 ? (
          Object.entries(companyNew).map(([key, newValue], i) => {
            const oldValue = (companyOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Company details</div>
        )}
      </>
    );

    const trustNew = pick<TrustEntityDetails, keyof TrustEntityDetails>(
      newEntity?.trust_entity_details || {},
      [
        'company_name',
        'country',
        'abn',
        'is_regulated',
        'type_original' ?? 'type',
        'registered_office',
      ],
    );
    const trustOld = pick<TrustEntityDetails, keyof TrustEntityDetails>(
      oldEntity?.trust_entity_details || {},
      [
        'company_name',
        'country',
        'abn',
        'is_regulated',
        'type_original' ?? 'type',
        'registered_office',
      ],
    );

    const trustDetails = (
      <>
        {Object.entries(trustNew).length > 0 ? (
          Object.entries(trustNew).map(([key, newValue], i) => {
            const oldValue = (trustOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Trust details</div>
        )}
      </>
    );

    const smsfNew = pick<TrustEntityDetails, keyof TrustEntityDetails>(
      newEntity?.smsf_entity_details || {},
      ['company_name', 'country', 'abn', 'registered_office'],
    );
    const smsfOld = pick<TrustEntityDetails, keyof TrustEntityDetails>(
      oldEntity?.smsf_entity_details || {},
      ['company_name', 'country', 'abn', 'registered_office'],
    );

    const smsfDetails = (
      <>
        {Object.entries(smsfNew).length > 0 ? (
          Object.entries(smsfNew).map(([key, newValue], i) => {
            const oldValue = (smsfOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">
            No information about Self Managed Superannuation Fund details
          </div>
        )}
      </>
    );

    const partnershipNew = pick<PartnershipEntityDetails, keyof PartnershipEntityDetails>(
      newEntity?.partnership_entity_details || {},
      [
        'company_name',
        'country',
        'abn',
        'entity_number',
        'partnership_type',
        'registered_office',
        'principal_place_of_business',
      ],
    );
    const partnershipOld = pick<PartnershipEntityDetails, keyof PartnershipEntityDetails>(
      oldEntity?.partnership_entity_details || {},
      [
        'company_name',
        'country',
        'abn',
        'entity_number',
        'partnership_type',
        'registered_office',
        'principal_place_of_business',
      ],
    );

    const partnershipDetails = (
      <>
        {Object.entries(partnershipNew).length > 0 ? (
          Object.entries(partnershipNew).map(([key, newValue], i) => {
            const oldValue = (partnershipOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Partnership details</div>
        )}
      </>
    );

    const associationNew = pick<AssociationEntityDetails, keyof AssociationEntityDetails>(
      newEntity?.association_entity_details_id || {},
      ['company_name', 'country', 'abn', 'registered_office', 'association_number', 'website'],
    );
    const associationOld = pick<AssociationEntityDetails, keyof AssociationEntityDetails>(
      oldEntity?.association_entity_details_id || {},
      ['company_name', 'country', 'abn', 'registered_office', 'association_number', 'website'],
    );

    const associationDetails = (
      <>
        {Object.entries(associationNew).length > 0 ? (
          Object.entries(associationNew).map(([key, newValue], i) => {
            const oldValue = (associationOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Association details</div>
        )}
      </>
    );

    const taxInformationNew = pick<TaxDetails, keyof TaxDetails>(
      newEntity?.company_entity_details ||
        newEntity?.trust_entity_details ||
        newEntity?.smsf_entity_details ||
        newEntity?.partnership_entity_details ||
        newEntity?.association_entity_details_id ||
        newEntity?.individual_entity_details ||
        {},
      [
        'account_holders_gin',
        'sponsoring_entity_name',
        'is_us_person',
        'non_us_person_certification',
        'tax_residence',
      ],
    );
    const taxInformationOld = pick<TaxDetails, keyof TaxDetails>(
      oldEntity?.company_entity_details ||
        oldEntity?.trust_entity_details ||
        oldEntity?.smsf_entity_details ||
        oldEntity?.partnership_entity_details ||
        oldEntity?.association_entity_details_id ||
        oldEntity?.individual_entity_details ||
        {},
      [
        'account_holders_gin',
        'sponsoring_entity_name',
        'is_us_person',
        'non_us_person_certification',
        'tax_residence',
      ],
    );

    const taxDetails = (
      <>
        {Object.entries(taxInformationNew).length > 0 ? (
          Object.entries(taxInformationNew)
            .filter(([key]) => key !== 'tax_residence')
            .map(([key, newValue], i) => {
              const oldValue = (taxInformationOld as any)[key];

              return (
                <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />
              );
            })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Tax Information</div>
        )}
      </>
    );

    const taxResidenceDetails = (
      <>
        {(taxInformationNew?.tax_residence?.length ?? 0) > 0 && (
          <div className="flex flex-col gap-4 text-sm">
            {taxInformationNew?.tax_residence?.map((residence, i) => {
              return (
                <div key={'residence' + i} className="flex flex-col gap-0 text-sm">
                  <div>Tax Residence #{i + 1}</div>
                  <div className="w-full grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 justify-between py-4">
                    {Object.entries(residence).map(([key, newValue], j) => {
                      const oldValue = (taxInformationOld?.tax_residence?.[i] as any)?.[key];

                      return (
                        <ItemOldAndNew
                          key={j}
                          oldValue={oldValue}
                          newValue={newValue}
                          keyValue={key}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </>
    );

    const advisorDetailsNew = pick<AdvisorDetails, keyof AdvisorDetails>(
      newEntity?.company_entity_details ||
        newEntity?.trust_entity_details ||
        newEntity?.smsf_entity_details ||
        newEntity?.partnership_entity_details ||
        newEntity?.association_entity_details_id ||
        newEntity?.individual_entity_details ||
        {},
      [
        'is_advisor',
        'is_not_authorized_representative',
        'is_not_copies',
        'is_only_authorized_representative',
        'is_financial_adviser_operate',
        'is_authorized_representative',
        'name_authorized_representative',
        'role_held_with_investor',
        'is_financial_adviser_nominated_by_investor',
        'is_investor_received_advise',
        'adviser',
      ],
    );

    const advisorDetailsOld = pick<AdvisorDetails, keyof AdvisorDetails>(
      oldEntity?.company_entity_details ||
        oldEntity?.trust_entity_details ||
        oldEntity?.smsf_entity_details ||
        oldEntity?.partnership_entity_details ||
        oldEntity?.association_entity_details_id ||
        oldEntity?.individual_entity_details ||
        {},
      [
        'is_advisor',
        'is_not_authorized_representative',
        'is_not_copies',
        'is_only_authorized_representative',
        'is_financial_adviser_operate',
        'is_authorized_representative',
        'name_authorized_representative',
        'role_held_with_investor',
        'is_financial_adviser_nominated_by_investor',
        'is_investor_received_advise',
        'adviser',
      ],
    );

    const advisorDetails = (
      <>
        {Object.entries(advisorDetailsNew).length > 0 ? (
          Object.entries(advisorDetailsNew)
            .filter(([key]) => key !== 'adviser')
            .map(([key, newValue], i) => {
              const oldValue = (advisorDetailsOld as any)[key];

              return (
                <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />
              );
            })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Advisor</div>
        )}
      </>
    );

    const adviserDetails = (
      <>
        {advisorDetailsNew?.adviser && (
          <div className="flex flex-col gap-0 text-sm">
            <div>Adviser Details</div>
            <div className="w-full grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 justify-between py-4">
              {Object.entries(advisorDetailsNew?.adviser || {}).length > 0 ? (
                Object.entries(advisorDetailsNew?.adviser || {}).map(([key, newValue], j) => {
                  const oldValue = (advisorDetailsOld?.adviser as any)?.[key];

                  return (
                    <ItemOldAndNew key={j} oldValue={oldValue} newValue={newValue} keyValue={key} />
                  );
                })
              ) : (
                <div className="w-[300px] text-gray-500">No information about Adviser</div>
              )}
            </div>
          </div>
        )}
      </>
    );

    const wholesaleDetailsNew = pick<WholesaleDetails, keyof WholesaleDetails>(
      newEntity?.company_entity_details ||
        newEntity?.trust_entity_details ||
        newEntity?.smsf_entity_details ||
        newEntity?.partnership_entity_details ||
        newEntity?.association_entity_details_id ||
        newEntity?.individual_entity_details ||
        {},
      ['is_wholesale', 'wholesale_type'],
    );

    const wholesaleDetailsOld = pick<WholesaleDetails, keyof WholesaleDetails>(
      oldEntity?.company_entity_details ||
        oldEntity?.trust_entity_details ||
        oldEntity?.smsf_entity_details ||
        oldEntity?.partnership_entity_details ||
        oldEntity?.association_entity_details_id ||
        oldEntity?.individual_entity_details ||
        {},
      ['is_wholesale', 'wholesale_type'],
    );

    const wholesaleDetails = (
      <>
        {Object.entries(wholesaleDetailsNew).length > 0 ? (
          Object.entries(wholesaleDetailsNew).map(([key, newValue], i) => {
            const oldValue = (wholesaleDetailsOld as any)[key];

            return <ItemOldAndNew key={i} oldValue={oldValue} newValue={newValue} keyValue={key} />;
          })
        ) : (
          <div className="w-[300px] text-gray-500">No information about Wholesale</div>
        )}
      </>
    );

    const info: JSX.Element = (
      <div className="border-t flex flex-col gap-4 w-full py-4 px-2">
        {!!item?.description && (
          <Alert
            variant="neutral-2"
            icon={AlertBlueIcon}
            label={item?.description}
            className="mb-0"
          />
        )}
        <span className="w-[200px]">
          <Select
            options={getSelectOptionByEnum<TabHolderInfo>(TabHolderInfo).filter(
              type =>
                type.value === TabHolderInfo.ContactDetails ||
                type.value === TabHolderInfo.BankDetails ||
                (type.value === TabHolderInfo.IndividualDetails &&
                  item?.new_data?.entity_data?.type === EntityType.Individual) ||
                (type.value === TabHolderInfo.CompanyDetails &&
                  item?.new_data?.entity_data?.type === EntityType.Company) ||
                (type.value === TabHolderInfo.TrustDetails &&
                  item?.new_data?.entity_data?.type === EntityType.Trust) ||
                (type.value === TabHolderInfo.SmsfDetails &&
                  item?.new_data?.entity_data?.type === EntityType.Smsf) ||
                (type.value === TabHolderInfo.PartnershipDetails &&
                  item?.new_data?.entity_data?.type === EntityType.Partnership) ||
                (type.value === TabHolderInfo.AssociationDetails &&
                  item?.new_data?.entity_data?.type === EntityType.Association) ||
                type.value === TabHolderInfo.TaxInformation ||
                type.value === TabHolderInfo.AdvisorDetails ||
                type.value === TabHolderInfo.WholesaleDetails,
            )}
            value={currentInfoTab}
            onChange={value => value && setCurrentInfoTab(value)}
            rounded="full"
            withBorder={true}
          />
        </span>
        {/* <div className="flex gap-x-20 gap-y-4 w-full flex-wrap justify-start"> */}
        <div className="w-full grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 justify-between py-4">
          {currentInfoTab === TabHolderInfo.ContactDetails && contactsDetails}
          {currentInfoTab === TabHolderInfo.BankDetails && bankDetails}
          {currentInfoTab === TabHolderInfo.IndividualDetails &&
            item?.new_data?.entity_data?.type === EntityType.Individual &&
            individualDetails}
          {currentInfoTab === TabHolderInfo.CompanyDetails &&
            item?.new_data?.entity_data?.type === EntityType.Company &&
            companyDetails}
          {currentInfoTab === TabHolderInfo.TrustDetails &&
            item?.new_data?.entity_data?.type === EntityType.Trust &&
            trustDetails}
          {currentInfoTab === TabHolderInfo.SmsfDetails &&
            item?.new_data?.entity_data?.type === EntityType.Smsf &&
            smsfDetails}
          {currentInfoTab === TabHolderInfo.PartnershipDetails &&
            item?.new_data?.entity_data?.type === EntityType.Partnership &&
            partnershipDetails}
          {currentInfoTab === TabHolderInfo.AssociationDetails &&
            item?.new_data?.entity_data?.type === EntityType.Association &&
            associationDetails}
          {currentInfoTab === TabHolderInfo.TaxInformation && taxDetails}
          {currentInfoTab === TabHolderInfo.AdvisorDetails && advisorDetails}
          {currentInfoTab === TabHolderInfo.WholesaleDetails && wholesaleDetails}
        </div>
        {currentInfoTab === TabHolderInfo.TaxInformation && taxResidenceDetails}
        {currentInfoTab === TabHolderInfo.AdvisorDetails && adviserDetails}
        {(status === InvestorEditsStatus.Pending || status === InvestorEditsStatus.InProgress) && (
          <div className="border-t border-t-gray-200 w-full pt-4 flex justify-between">
            <div></div>
            <div className="flex gap-4">
              {status === InvestorEditsStatus.Pending && userRole === Role.SR_ISSUER_ADMIN && (
                <>
                  <Button
                    variant="secondary"
                    className="w-[100px]"
                    onClick={handleOpenRejectEntityModal}
                    loading={isLoadingChangeStatus}
                    disabled={isLoadingChangeStatus}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="primary"
                    className="w-[100px]"
                    onClick={handleChangeStatus(InvestorEditsStatus.Approved)}
                    loading={isLoadingChangeStatus}
                    disabled={isLoadingChangeStatus}
                  >
                    Confirm
                  </Button>
                </>
              )}
              {status === InvestorEditsStatus.InProgress && (
                <>
                  <Button variant="secondary" className="w-[200px]" onClick={handleContinueEdit}>
                    Continue filling in data
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );

    return info;
  }, [
    currentInfoTab,
    handleChangeStatus,
    handleContinueEdit,
    handleOpenRejectEntityModal,
    isLoadingChangeStatus,
    item?.description,
    item?.new_data?.entity_data,
    item?.old_data?.entity_data,
    status,
    userRole,
  ]);

  return (
    <DisclosureListItem
      leftMainString={
        <>
          {item?.new_data?.entity_data?.entity_name || 'Entity name is unset'}{' '}
          {!!item?.new_data?.entity_data?.type &&
            '(' + fixNameEnum(item?.new_data?.entity_data?.type) + ')'}
        </>
      }
      leftSubduedContentSet={[dateToPrint(item?.created_at, DateVariant.Dashboard, true)]}
      classes={{
        rightMainString: 'text-nowrap text-right',
        rightContentUp: 'justify-items-end',
        leftContent: 'w-[200px]',
      }}
      rightContent={
        <div className="flex gap-8 items-center">
          {item.edited_by_user?.first_name && item.edited_by !== item.user_id && (
            <div className="flex flex-col">
              <span className="text-gray-900 text-nowrap text-right text-[14px] leading-[22px]">{`${item.edited_by_user?.first_name} ${item.edited_by_user?.last_name}`}</span>
              <span className="text-gray-500 text-[12px] leading-[20px]">Edited by</span>
            </div>
          )}
          <div
            className={classNames(
              'text-[12px] leading-[20px] text-white',
              'rounded-[1000px] px-2 py-[2px] capitalize h-6 w-fit',
              {
                'bg-success-green': status === InvestorEditsStatus.Approved,
                'bg-blue-700': status === InvestorEditsStatus.Pending,
                'bg-warning-orange': status === InvestorEditsStatus.InProgress,
                'bg-error-red': status === InvestorEditsStatus.Rejected,
              },
            )}
          >
            <span className="flex flex-row space-y-5 justify-center items-center">
              {status === InvestorEditsStatus.InProgress && 'In Progress'}
              {status !== InvestorEditsStatus.InProgress && fixNameEnum(status)}
              {status &&
                [InvestorEditsStatus.Rejected].includes(status) &&
                entity_application_rejection_reason && (
                  <TooltipNew className="normal-case" text={entity_application_rejection_reason}>
                    <img src={AlertWhiteIcon} className="text-sm ml-1 max-w-[16px]" alt="info" />
                  </TooltipNew>
                )}
            </span>
          </div>
        </div>
      }
      rightMainString={
        <span className="text-gray-900">{`${item.user?.first_name} ${item.user?.last_name}`}</span>
      }
      rightSubduedString={<span className="text-gray-500">Requested by</span>}
      bottomCustomContent={bottomCustomContent}
    />
  );
};

interface ItemOldAndNewProps {
  keyValue: string;
  oldValue: any;
  newValue: any;
}

const ItemOldAndNew = ({
  oldValue: oldValueCurr,
  newValue: newValueCurr,
  keyValue: key,
}: ItemOldAndNewProps) => {
  const { value: newValue } = addEnumToValue(fixNameEnum(key), newValueCurr);
  const { value: oldValue } = addEnumToValue(fixNameEnum(key), oldValueCurr);
  return (
    <>
      {newValue !== undefined &&
        newValue !== '' &&
        ((isArray(newValue) && newValue?.length > 0) || !isArray(newValue)) && (
          <div className="flex flex-col">
            <span className="text-xs text-gray-500">{fixNameEnum(key)}</span>
            {oldValue !== undefined &&
              oldValue !== '' &&
              oldValue !== newValue &&
              ((isArray(oldValue) && oldValue?.length > 0) || (!isArray(oldValue) && oldValue)) && (
                <span className="text-sm leading-[22px] text-gray-500 line-through">
                  {typeof oldValue === 'boolean' ? (oldValue ? 'Yes' : 'No') : oldValue ?? ''}
                </span>
              )}
            <span className="text-sm leading-[22px] text-gray-900">
              {typeof newValue === 'boolean' ? (newValue ? 'Yes' : 'No') : newValue ?? ''}
            </span>
          </div>
        )}
    </>
  );
};
